// frontend/src/components/Test/TestView.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import TestInstructions from './TestInstructions';
import FotoCaptura from './FotoCaptura';
import TestQuestion from './TestQuestion';
import TestComplete from './TestComplete';
import SerieDescanso from './SerieDescanso';
import testService from '../../services/testService';
import './TestStyles.css';

const TestView = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  // Estado principal
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // La empresa no tiene créditos: se muestra un aviso, no un "Error" técnico
  const [errorSinCreditos, setErrorSinCreditos] = useState(false);
  const [testData, setTestData] = useState(null);
  const [testCompleted, setTestCompleted] = useState(false);
  const [showingInstructions, setShowingInstructions] = useState(true);
  const [fotoCapturada, setFotoCapturada] = useState(false);
  
  // Estado de prueba actual
  const [currentTestIndex, setCurrentTestIndex] = useState(0);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  
  // Estado para series (TERMAN)
  const [series, setSeries] = useState([]);
  const [currentSerie, setCurrentSerie] = useState(null);
  const [mostrandoDescansoSerie, setMostrandoDescansoSerie] = useState(false);
  const [siguienteSerieDescanso, setSiguienteSerieDescanso] = useState(null);

  // Estado del timer
  const [tiempoRestante, setTiempoRestante] = useState(0);
  const timerIntervalRef = useRef(null);
  const timerActiveRef = useRef(false);
  const [timerActive, setTimerActive] = useState(false);

  // Referencias "siempre actuales" para evitar closures obsoletas dentro del
  // callback del setInterval (que puede quedar congelado desde que inició la
  // prueba): se actualizan en cada render, así el timeout de cada serie
  // siempre usa el testData/índice/función más recientes, no los del inicio.
  const testDataRef = useRef(null);
  const currentTestIndexRef = useRef(0);
  const handleSiguienteSerieRef = useRef(null);
  const finalizeTestRef = useRef(null);

  // Estado de carga inicial
  const [respuestasPrevias, setRespuestasPrevias] = useState(null);
  
  // Limpiar timer de forma segura
  const clearTimer = useCallback(() => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    timerActiveRef.current = false;
    setTimerActive(false);
  }, []);
  
  // Iniciar timer de forma segura
  const startTimer = useCallback((segundos, onTimeout = null) => {
    console.log('🕐 Iniciando timer con:', segundos, 'segundos');
    
    clearTimer();
    
    if (segundos && segundos > 0) {
      setTiempoRestante(segundos);
      timerActiveRef.current = true;
      setTimerActive(true);
      
      timerIntervalRef.current = setInterval(() => {
        if (timerActiveRef.current) {
          setTiempoRestante(prev => {
            if (prev <= 1) {
              clearTimer();
              
              if (onTimeout) {
                onTimeout();
              } else {
                const currentPrueba = testDataRef.current?.pruebas?.[currentTestIndexRef.current];
                const esOtis = currentPrueba?.clave === 'OTIS';
                const esCleaver = currentPrueba?.clave === 'CLEAVER';

                if (esOtis || esCleaver) {
                  alert('⏰ Tiempo agotado para esta prueba. Se finalizará automáticamente.');
                  finalizeTestRef.current();
                } else {
                  alert('⏰ Tiempo agotado para esta serie.');
                  handleSiguienteSerieRef.current();
                }
              }
              return 0;
            }
            return prev - 1;
          });
        }
      }, 1000);
    }
  }, [clearTimer]);
  
  // Cargar datos iniciales
  useEffect(() => {
    if (!token) {
      setError('No se proporcionó un token válido');
      setLoading(false);
      return;
    }
    loadTestData();
  }, [token]);
  
  // Limpiar timer al desmontar
  useEffect(() => {
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, []);

  // Vigilancia de integridad: únicamente durante pruebas de habilidad (OTIS/
  // Terman-Merrill), donde sí hay una respuesta objetivamente correcta que
  // podría buscarse en otra pestaña. No restringe nada ni bloquea la
  // prueba: solo avisa al candidato (con un tono cada vez más serio) y deja
  // registro en el servidor para que reclutamiento lo revise.
  useEffect(() => {
    const claveActual = testData?.pruebas?.[currentTestIndex]?.clave;
    const esPruebaVigilada = claveActual === 'OTIS' || claveActual === 'TERMAN';

    if (!esPruebaVigilada || showingInstructions || testCompleted || !token) {
      return;
    }

    const handleVisibilityChange = async () => {
      if (!document.hidden) return;

      try {
        const response = await fetch('/api/test/alerta-cambio-pestana', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, prueba_clave: claveActual })
        });
        const data = await response.json();
        const conteo = data.conteo || 1;

        if (conteo === 1) {
          alert('⚠️ Detectamos que saliste de esta pantalla durante una prueba de habilidad.\n\nEstamos evaluando tu comportamiento durante esta prueba. Si esto se repite, podría considerarse en tu evaluación final.');
        } else if (conteo === 2) {
          alert('🚨 Has salido de esta pantalla por segunda vez.\n\nEste comportamiento ya está siendo registrado y puede afectar negativamente el resultado de tu evaluación si continúa.');
        } else {
          alert('🚨 Esta es la tercera vez que sales de esta pantalla durante la prueba.\n\nHemos notificado al equipo de reclutamiento sobre esta conducta.');
        }
      } catch (error) {
        console.error('Error al registrar alerta de integridad:', error);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [testData, currentTestIndex, showingInstructions, testCompleted, token]);

  const loadTestData = async () => {
    try {
      const data = await testService.verifyToken(token);
      setTestData(data);
      setFotoCapturada(!!data.asignacion?.foto_capturada);
      await cargarRespuestasPrevias();
      setLoading(false);
    } catch (err) {
      setError(err.error || 'Error al cargar la prueba');
      setLoading(false);
    }
  };
  
  const cargarRespuestasPrevias = async () => {
    try {
      const respuestas = await testService.getRespuestasByToken(token);
      if (respuestas && respuestas.length > 0) {
        console.log('Respuestas previas cargadas:', respuestas.length);
        setRespuestasPrevias(respuestas);
        
        const respuestasMap = {};
        respuestas.forEach(resp => {
          respuestasMap[resp.id_reactivo] = resp.respuesta;
        });
        setAnswers(respuestasMap);
      }
    } catch (err) {
      console.error('Error cargando respuestas:', err);
    }
  };
  
  const startTest = async () => {
    // Cobro de créditos al iniciar (idempotente). Sin créditos, el candidato
    // ve un mensaje en lugar de la prueba.
    try {
      await testService.iniciarTest(token);
    } catch (err) {
      setError(err.error || 'No se pudo iniciar la evaluación');
      setErrorSinCreditos(Boolean(err.sin_creditos));
      return;
    }
    setShowingInstructions(false);
    
    if (respuestasPrevias && respuestasPrevias.length > 0) {
      await restaurarProgreso();
    } else {
      await loadTestQuestions(0);
    }
  };
  
  const restaurarProgreso = async () => {
    console.log('=== RESTAURANDO PROGRESO ===');

    // Usar el estado_prueba que ya calcula el backend (más confiable que
    // re-derivarlo aquí): busca la prueba en progreso, o si no hay ninguna
    // marcada así, la primera que no esté completada.
    let pruebaIndex = testData.pruebas.findIndex(p => p.estado_prueba === 'en_progreso');
    if (pruebaIndex === -1) {
      pruebaIndex = testData.pruebas.findIndex(p => p.estado_prueba !== 'completada');
    }
    if (pruebaIndex === -1) {
      pruebaIndex = testData.pruebas.length - 1;
    }

    setCurrentTestIndex(pruebaIndex);
    await loadTestQuestions(pruebaIndex, true);
  };
  
  const loadTestQuestions = async (testIndex, isRestore = false) => {
    try {
      setLoadingQuestions(true);
      const prueba = testData.pruebas[testIndex];
      
      clearTimer();
      setSeries([]);
      setCurrentSerie(null);
      setQuestions([]);
      setCurrentQuestionIndex(0);
      
      // Determinar tipo de prueba
      if (prueba.clave === 'TERMAN') {
        await loadTermanTest(prueba, isRestore);
      } else if (prueba.clave === 'CLEAVER') {
        await loadCleaverTest(prueba, isRestore);
      } else {
        await loadGeneralTest(prueba, isRestore);
      }
      
      setLoadingQuestions(false);
    } catch (err) {
      console.error('Error cargando preguntas:', err);
      setError(err.error || 'Error al cargar las preguntas');
      setLoadingQuestions(false);
    }
  };
  
  // ============================================
  // CLEAVER TEST (selección forzada: 21 bloques de 4 palabras D/I/S/C)
  // ============================================
  const ORDEN_FACTOR_CLEAVER = { D: 0, I: 1, S: 2, C: 3 };
  const TEXTO_BLOQUE_CLEAVER = 'De las siguientes palabras, indica cuál te describe MÁS y cuál te describe MENOS:';

  const construirBloquesCleaver = (reactivos) => {
    const bloquesMap = new Map();
    for (const r of reactivos) {
      const numeroBloque = Math.ceil(r.numero_reactivo / 4);
      if (!bloquesMap.has(numeroBloque)) bloquesMap.set(numeroBloque, []);
      bloquesMap.get(numeroBloque).push(r);
    }
    return Array.from(bloquesMap.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([numero, palabras]) => ({
        id: numero,
        numero_reactivo: numero,
        esBloqueCleaver: true,
        texto: TEXTO_BLOQUE_CLEAVER,
        palabras: [...palabras].sort(
          (a, b) => ORDEN_FACTOR_CLEAVER[a.factor] - ORDEN_FACTOR_CLEAVER[b.factor]
        ),
      }));
  };

  const loadCleaverTest = async (prueba, isRestore) => {
    console.log('=== CARGANDO CLEAVER (bloques) ===');

    const reactivos = await testService.getReactivos(prueba.prueba_id);
    const bloques = construirBloquesCleaver(reactivos);

    let indiceInicial = 0;
    if (isRestore && respuestasPrevias) {
      const respuestasPrueba = respuestasPrevias.filter(r => r.prueba_id === prueba.prueba_id);
      const conteoPorBloque = {};
      for (const r of respuestasPrueba) {
        const b = Math.ceil(r.numero_reactivo / 4);
        conteoPorBloque[b] = (conteoPorBloque[b] || 0) + 1;
      }
      // Un bloque está completo cuando tiene sus 2 respuestas (Más y Menos)
      while (indiceInicial < bloques.length && (conteoPorBloque[bloques[indiceInicial].id] || 0) >= 2) {
        indiceInicial++;
      }
    }

    if (indiceInicial >= bloques.length) {
      await siguientePrueba();
      return;
    }

    setQuestions(bloques);
    setCurrentQuestionIndex(indiceInicial);

    if (prueba.tiempo_limite_segundos > 0) {
      startTimer(prueba.tiempo_limite_segundos, () => {
        alert('⏰ Tiempo agotado para esta prueba. Se finalizará automáticamente.');
        finalizeTest();
      });
    }
  };

  const guardarRespuestaBloqueCleaver = async (bloque, seleccion) => {
    const prueba = testData.pruebas[currentTestIndex];
    let tiempo_ms = 0;
    if (prueba.tiempo_limite_segundos > 0) {
      tiempo_ms = (prueba.tiempo_limite_segundos - tiempoRestante) * 1000;
    }

    try {
      await testService.saveRespuesta(token, seleccion.masId, 'M', tiempo_ms, null);
      await testService.saveRespuesta(token, seleccion.menosId, 'L', tiempo_ms, null);
      setAnswers(prev => ({ ...prev, [seleccion.masId]: 'M', [seleccion.menosId]: 'L' }));
      console.log('✅ Bloque Cleaver guardado:', bloque.id, seleccion);
      return true;
    } catch (err) {
      console.error('❌ Error guardando bloque de Cleaver:', err);
      return false;
    }
  };

  // ============================================
  // TERMAN TEST
  // ============================================
  const loadTermanTest = async (prueba, isRestore) => {
    console.log('=== CARGANDO TERMAN ===');
    
    const seriesData = await testService.getSeries(prueba.prueba_id);
    
    if (seriesData && seriesData.length > 0) {
      setSeries(seriesData);
      
      if (isRestore && respuestasPrevias) {
        await restaurarProgresoTerman(prueba, seriesData);
      } else {
        const primeraSerie = seriesData[0];
        setCurrentSerie(primeraSerie);
        
        const todasLasPreguntas = await testService.getReactivos(prueba.prueba_id);
        const preguntasSerie = todasLasPreguntas.filter(q => 
          q.numero_reactivo >= primeraSerie.numero_inicial && 
          q.numero_reactivo <= primeraSerie.numero_final
        );
        
        preguntasSerie.sort((a, b) => a.numero_reactivo - b.numero_reactivo);
        setQuestions(preguntasSerie);
        setCurrentQuestionIndex(0);
        
        if (primeraSerie.tiempo_limite_segundos > 0) {
          startTimer(primeraSerie.tiempo_limite_segundos);
        }
      }
    } else {
      // Fallback: cargar todas las preguntas
      const reactivos = await testService.getReactivos(prueba.prueba_id);
      setQuestions(reactivos);
      setCurrentQuestionIndex(0);
    }
  };
  
  const restaurarProgresoTerman = async (prueba, seriesData) => {
    const respuestasPrueba = respuestasPrevias.filter(r => r.prueba_id === prueba.prueba_id);

    // Encontrar última respuesta
    let ultimoNumeroReactivo = 0;
    for (const resp of respuestasPrueba) {
      if (resp.numero_reactivo > ultimoNumeroReactivo) {
        ultimoNumeroReactivo = resp.numero_reactivo;
      }
    }

    // Encontrar serie actual
    let serieIndex = 0;
    for (let i = 0; i < seriesData.length; i++) {
      const serie = seriesData[i];
      if (ultimoNumeroReactivo >= serie.numero_inicial && ultimoNumeroReactivo <= serie.numero_final) {
        serieIndex = i;
        break;
      }
    }

    const serieActual = seriesData[serieIndex];

    // Cargar preguntas de la serie encontrada
    const todasLasPreguntas = await testService.getReactivos(prueba.prueba_id);
    const preguntasSerie = todasLasPreguntas.filter(q =>
      q.numero_reactivo >= serieActual.numero_inicial &&
      q.numero_reactivo <= serieActual.numero_final
    );

    preguntasSerie.sort((a, b) => a.numero_reactivo - b.numero_reactivo);

    const respondidosSet = new Set(respuestasPrueba.map(r => r.id_reactivo));
    const serieCompleta = preguntasSerie.every(q => respondidosSet.has(q.id));

    if (serieCompleta) {
      // Ya respondió todas las preguntas de esta serie: el punto de reanudación
      // correcto es la pantalla de descanso de la siguiente serie (o avanzar de
      // prueba si esta era la última), no repetir preguntas ya contestadas.
      setCurrentSerie(serieActual);
      if (serieIndex + 1 < seriesData.length) {
        mostrarDescansoSerie(seriesData[serieIndex + 1]);
      } else {
        await siguientePrueba();
      }
      return;
    }

    setCurrentSerie(serieActual);

    // Encontrar pregunta actual dentro de esta serie
    let preguntaIndex = 0;
    for (let i = 0; i < preguntasSerie.length; i++) {
      if (!respondidosSet.has(preguntasSerie[i].id)) {
        preguntaIndex = i;
        break;
      }
    }

    setQuestions(preguntasSerie);
    setCurrentQuestionIndex(preguntaIndex);

    // Calcular tiempo restante SOLO con el tiempo usado en ESTA serie
    // (antes se sumaba el tiempo de todas las series de la prueba, lo cual
    // agotaba el timer de inmediato al reanudar en series posteriores).
    let tiempoUsado = 0;
    for (const resp of respuestasPrueba) {
      if (resp.numero_reactivo >= serieActual.numero_inicial && resp.numero_reactivo <= serieActual.numero_final) {
        tiempoUsado += resp.tiempo_ms || 0;
      }
    }

    const tiempoRestanteServ = Math.max(serieActual.tiempo_limite_segundos - Math.floor(tiempoUsado / 1000), 0);
    if (tiempoRestanteServ > 0) {
      startTimer(tiempoRestanteServ);
    } else {
      // El tiempo ya se había agotado antes de la desconexión: avanzar igual.
      // No se usa handleSiguienteSerie() aquí porque depende del estado
      // `series` por closure, que en este punto todavía no refleja el
      // setSeries(seriesData) de loadTermanTest (no ha habido re-render) —
      // usar `seriesData`/`serieIndex`, ya locales a esta función, evita que
      // se interprete erróneamente como "fin de la prueba" y regrese a
      // instrucciones en vez de pasar a la siguiente serie.
      if (serieIndex + 1 < seriesData.length) {
        mostrarDescansoSerie(seriesData[serieIndex + 1]);
      } else {
        await siguientePrueba();
      }
    }
  };

  // ============================================
  // PRUEBAS GENERALES (OTIS, 16FP)
  // ============================================
  const loadGeneralTest = async (prueba, isRestore) => {
    console.log('=== CARGANDO PRUEBA GENERAL ===', prueba.nombre);
    
    const reactivos = await testService.getReactivos(prueba.prueba_id);
    reactivos.sort((a, b) => a.numero_reactivo - b.numero_reactivo);
    
    if (isRestore && respuestasPrevias) {
      const respuestasPrueba = respuestasPrevias.filter(r => r.prueba_id === prueba.prueba_id);
      const respondidosSet = new Set(respuestasPrueba.map(r => r.id_reactivo));
      
      let siguienteIndice = 0;
      for (let i = 0; i < reactivos.length; i++) {
        if (!respondidosSet.has(reactivos[i].id)) {
          siguienteIndice = i;
          break;
        }
        if (i === reactivos.length - 1) {
          siguienteIndice = reactivos.length;
        }
      }
      
      setQuestions(reactivos);
      setCurrentQuestionIndex(siguienteIndice);
      
      if (siguienteIndice >= reactivos.length) {
        await siguientePrueba();
        return;
      }
      
      // Timer para OTIS
      const esOtis = prueba.clave === 'OTIS';
      if (esOtis && prueba.tiempo_limite_segundos > 0) {
        let tiempoUsado = 0;
        for (const resp of respuestasPrueba) {
          tiempoUsado += resp.tiempo_ms || 0;
        }
        const tiempoRestante = Math.max(prueba.tiempo_limite_segundos - Math.floor(tiempoUsado / 1000), 0);
        
        if (tiempoRestante > 0) {
          startTimer(tiempoRestante);
        } else {
          await finalizeTest();
        }
      }
    } else {
      setQuestions(reactivos);
      setCurrentQuestionIndex(0);
      
      const esOtis = prueba.clave === 'OTIS';
      if (esOtis && prueba.tiempo_limite_segundos > 0) {
        startTimer(prueba.tiempo_limite_segundos);
      }
    }
  };
  
  // ============================================
  // GUARDAR RESPUESTA Y NAVEGACIÓN
  // ============================================
  const saveAnswer = async (id_reactivo, respuesta) => {
    let tiempo_ms = 0;
    const prueba = testData.pruebas[currentTestIndex];
    const esOtis = prueba.clave === 'OTIS';
    const esCleaver = prueba.clave === 'CLEAVER';
    
    if ((esOtis || esCleaver) && prueba.tiempo_limite_segundos > 0) {
      tiempo_ms = (prueba.tiempo_limite_segundos - tiempoRestante) * 1000;
    } else if (currentSerie && currentSerie.tiempo_limite_segundos > 0) {
      tiempo_ms = (currentSerie.tiempo_limite_segundos - tiempoRestante) * 1000;
    }

    try {
      const result = await testService.saveRespuesta(token, id_reactivo, respuesta, tiempo_ms, null);
      setAnswers(prev => ({ ...prev, [id_reactivo]: respuesta }));
      console.log('✅ Respuesta guardada:', id_reactivo, respuesta);
      return true;
    } catch (err) {
      console.error('❌ Error guardando respuesta:', err);
      return false;
    }
  };
  
const handleAnswer = async (respuesta) => {
    console.log('=== handleAnswer ===');
    console.log('questions.length:', questions.length);
    console.log('currentQuestionIndex actual:', currentQuestionIndex);
    
    if (!questions || questions.length === 0) {
        console.log('No hay preguntas');
        return;
    }
    
    const currentQuestion = questions[currentQuestionIndex];
    if (!currentQuestion) {
        console.log('No hay pregunta actual');
        return;
    }
    
    console.log('Pregunta actual:', currentQuestion.numero_reactivo);
    console.log('Respuesta seleccionada:', respuesta);

    const saved = currentQuestion.esBloqueCleaver
        ? await guardarRespuestaBloqueCleaver(currentQuestion, respuesta)
        : await saveAnswer(currentQuestion.id, respuesta);
    console.log('¿Respuesta guardada?', saved);

    if (saved) {
        if (currentQuestionIndex + 1 < questions.length) {
            console.log('✅ Avanzando a pregunta:', currentQuestionIndex + 1);
            setCurrentQuestionIndex(currentQuestionIndex + 1);
        } else {
            console.log('📌 Última pregunta completada de esta prueba/serie');

            // 🔴 VERIFICAR si hay más series (TERMAN)
            const currentPrueba = testData?.pruebas?.[currentTestIndex];
            const esTerman = currentPrueba?.clave === 'TERMAN';

            if (esTerman && series.length > 0 && currentSerie) {
                // Verificar si hay más series en Terman
                const currentIndex = series.findIndex(s => s.id === currentSerie.id);
                if (currentIndex + 1 < series.length) {
                    console.log('🔄 Pasando a siguiente serie de Terman');
                    await handleSiguienteSerie();
                } else {
                    console.log('✅ Todas las series de Terman completadas');
                    await siguientePrueba();
                }
            } else {
                // Para OTIS, Cleaver, 16FP u otras pruebas sin series
                console.log('✅ Prueba completada, pasando a la siguiente');
                await siguientePrueba();
            }
        }
    } else {
        console.log('❌ Error al guardar, no se avanza');
        alert('Error al guardar la respuesta. Por favor, intenta de nuevo.');
    }
};

  // Muestra la pantalla de descanso con las instrucciones de la siguiente
  // serie, en vez de arrancarla directamente. Deja un punto de reanudación
  // claro: mientras el candidato esté aquí, ningún timer está corriendo.
  const mostrarDescansoSerie = (nextSerie) => {
    clearTimer();
    setQuestions([]);
    setCurrentQuestionIndex(0);
    setSiguienteSerieDescanso(nextSerie);
    setMostrandoDescansoSerie(true);
  };

  const handleSiguienteSerie = async () => {
    const prueba = testData.pruebas[currentTestIndex];

    if (prueba.clave === 'TERMAN' && series.length > 0 && currentSerie) {
      const currentIndex = series.findIndex(s => s.id === currentSerie.id);

      if (currentIndex + 1 < series.length) {
        mostrarDescansoSerie(series[currentIndex + 1]);
      } else {
        await siguientePrueba();
      }
    } else {
      await siguientePrueba();
    }
  };

  // Se ejecuta cuando el candidato confirma en la pantalla de descanso que
  // quiere comenzar la siguiente serie. Aquí sí arranca el timer.
  const comenzarSiguienteSerie = async () => {
    const prueba = testData.pruebas[currentTestIndex];
    const nextSerie = siguienteSerieDescanso;
    if (!nextSerie) return;

    setMostrandoDescansoSerie(false);
    setSiguienteSerieDescanso(null);
    setCurrentSerie(nextSerie);

    const todasLasPreguntas = await testService.getReactivos(prueba.prueba_id);
    const preguntasSerie = todasLasPreguntas.filter(q =>
      q.numero_reactivo >= nextSerie.numero_inicial &&
      q.numero_reactivo <= nextSerie.numero_final
    );

    preguntasSerie.sort((a, b) => a.numero_reactivo - b.numero_reactivo);
    setQuestions(preguntasSerie);
    setCurrentQuestionIndex(0);

    clearTimer();
    if (nextSerie.tiempo_limite_segundos > 0) {
      startTimer(nextSerie.tiempo_limite_segundos);
    }
  };

  // Al terminar una prueba completa (no una serie de Terman), regresa a la
  // pantalla de instrucciones/checklist en vez de saltar directo a la
  // siguiente. Es el mismo punto de reanudación que ya existía al cerrar y
  // volver a abrir la página, pero ahora aparece de inmediato: sirve de
  // pausa natural (ej. para ir al baño) entre una prueba y otra.
  const volverAInstruccionesEntrePruebas = async () => {
    clearTimer();
    // Se activa el indicador de carga antes de vaciar las preguntas: sin
    // esto, entre el setQuestions([]) de aquí abajo y que termine de llegar
    // el testData/respuestas más reciente, el render caía un instante en la
    // pantalla roja de "No hay preguntas disponibles" (un parpadeo visible)
    // antes de mostrar Instrucciones.
    setLoadingQuestions(true);
    setQuestions([]);
    setCurrentQuestionIndex(0);
    setSeries([]);
    setCurrentSerie(null);

    try {
      const data = await testService.verifyToken(token);
      setTestData(data);
      await cargarRespuestasPrevias();
    } catch (err) {
      console.error('Error actualizando estado entre pruebas:', err);
    }

    setShowingInstructions(true);
    setLoadingQuestions(false);
  };

  const siguientePrueba = async () => {
    console.log('=== siguientePrueba ===');
    console.log('Current test index:', currentTestIndex);
    console.log('Total pruebas:', testData?.pruebas?.length);

    clearTimer();

    // Se llega aquí únicamente cuando ya no queda nada más que mostrarle al
    // candidato en la prueba actual (preguntas/series agotadas), aunque no
    // se haya respondido el 100% (normal en Terman por el límite de tiempo
    // por serie). Avisamos al backend ANTES de refrescar testData para que
    // /verify ya no la reporte como en_progreso — si no, el candidato queda
    // en un loop entre esta prueba y la pantalla de instrucciones.
    const pruebaActual = testData?.pruebas?.[currentTestIndex];
    if (pruebaActual) {
      await testService.finalizarPrueba(token, pruebaActual.prueba_id);
    }

    if (currentTestIndex + 1 < testData.pruebas.length) {
        console.log('🔄 Regresando a instrucciones antes de la siguiente prueba');
        await volverAInstruccionesEntrePruebas();
    } else {
        console.log('🏁 No hay más pruebas, finalizando...');
        await finalizeTest();
    }
};
  
  const finalizeTest = async () => {
    try {
      clearTimer();
      await testService.finalizarTest(token);
      setTestCompleted(true);
    } catch (err) {
      console.error('Error finalizando prueba:', err);
      alert('Error al finalizar la prueba. Por favor, contacta al administrador.');
    }
  };

  // Se ejecuta después de cada render (sin arreglo de dependencias a propósito)
  // para que estas referencias siempre apunten a los valores/funciones más
  // recientes, sin importar cuándo se creó el temporizador que las consulta.
  useEffect(() => {
    testDataRef.current = testData;
    currentTestIndexRef.current = currentTestIndex;
    handleSiguienteSerieRef.current = handleSiguienteSerie;
    finalizeTestRef.current = finalizeTest;
  });

  const formatTiempo = (segundos) => {
    const mins = Math.floor(segundos / 60);
    const secs = segundos % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  // ============================================
  // RENDERIZADO
  // ============================================
  if (loading) {
    return (
      <div className="test-loading">
        <div className="spinner"></div>
        <p>Cargando prueba...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="test-error">
        <h2>{errorSinCreditos ? 'Evaluación no disponible' : 'Error'}</h2>
        <p>{error}</p>
        <button onClick={() => window.location.reload()} className="btn-start">
          Reintentar
        </button>
      </div>
    );
  }
  
  if (testCompleted) {
    return <TestComplete />;
  }
  
  if (showingInstructions) {
    if (!fotoCapturada) {
      return <FotoCaptura token={token} onCompletado={() => setFotoCapturada(true)} />;
    }
    return <TestInstructions testData={testData} onStart={startTest} />;
  }
  
  if (loadingQuestions) {
    return (
      <div className="test-loading">
        <div className="spinner"></div>
        <p>Cargando preguntas...</p>
      </div>
    );
  }

  if (mostrandoDescansoSerie && siguienteSerieDescanso) {
    return (
      <SerieDescanso
        serieCompletada={currentSerie}
        siguienteSerie={siguienteSerieDescanso}
        totalSeries={series.length}
        onContinuar={comenzarSiguienteSerie}
      />
    );
  }
  
  if (!questions || questions.length === 0) {
    return (
      <div className="test-error">
        <h2>Error</h2>
        <p>No hay preguntas disponibles para esta prueba.</p>
      </div>
    );
  }
  
  const currentPrueba = testData.pruebas[currentTestIndex];
  const currentQuestion = questions[currentQuestionIndex];
  
  if (!currentQuestion) {
    return (
      <div className="test-error">
        <h2>Error</h2>
        <p>Error al cargar la pregunta actual.</p>
      </div>
    );
  }
  
  // Calcular progreso
  let progress = 0;
  const esTerman = currentPrueba?.clave === 'TERMAN';
  const esCleaver = currentPrueba?.clave === 'CLEAVER';
  const esOtis = currentPrueba?.clave === 'OTIS';
  const esPersonalizada = currentPrueba?.clave === 'PERSONALIZADA';
  const MODO_POR_TIPO_PREGUNTA = {
    opcion_unica: 'opcion-simple',
    opcion_multiple: 'opcion-multiple',
    texto_abierto: 'texto-abierto',
    escala: 'escala'
  };
  
  const showTimer = timerActive && tiempoRestante > 0 &&
    ((esTerman && currentSerie?.tiempo_limite_segundos > 0) ||
     esCleaver ||
     esOtis);

  if (esTerman && series.length > 0 && currentSerie) {
    const serieIndex = series.findIndex(s => s.id === currentSerie.id);
    const progresoSerie = ((currentQuestionIndex + 1) / questions.length) * 100;
    progress = (serieIndex / series.length) * 100 + (progresoSerie / series.length);
  } else {
    progress = ((currentQuestionIndex + 1) / questions.length) * 100;
  }
  
  return (
    <div className="test-question-container">
      <div className="progress-header">
        <div className="test-info">
          <span className="test-name">{currentPrueba.nombre}</span>

           {esCleaver && (
          <span className="section-badge">
            📚 Bloque {currentQuestionIndex + 1} de {questions.length}
          </span>
        )}

          <span className="test-counter">
            Prueba {currentTestIndex + 1} de {testData.pruebas.length}
          </span>
        </div>

        {esTerman && currentSerie && (
          <div className="serie-badge-row">
            <span className="serie-badge">
              Serie {currentSerie.nombre_serie}: {currentSerie.descripcion}
            </span>
          </div>
        )}

        {showTimer && (
          <div className="timer-container">
            <div className={`timer-clock ${tiempoRestante <= 60 ? 'warning' : ''} ${tiempoRestante <= 30 ? 'danger' : ''}`}>
              <span className="timer-icon">⏱️</span>
              <span className="timer-label">
                {esOtis || esCleaver ? ' ' : 'Tiempo restante:'}
              </span>
              <span className="timer-value">{formatTiempo(tiempoRestante)}</span>
            </div>
          </div>
        )}
        
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${progress}%` }}></div>
        </div>
      </div>
      
      <TestQuestion
        question={currentQuestion}
        currentNumber={currentQuestionIndex + 1}
        totalQuestions={questions.length}
        onAnswer={handleAnswer}
        modoRespuesta={
          esTerman && currentSerie?.nombre_serie === 'IV' ? 'opcion-doble' :
          esTerman && currentSerie?.nombre_serie === 'V' ? 'texto-simple' :
          esTerman && currentSerie?.nombre_serie === 'X' ? 'texto-doble' :
          esCleaver ? 'cleaver-bloque' :
          esPersonalizada ? (MODO_POR_TIPO_PREGUNTA[currentQuestion.tipo_pregunta] || 'opcion-simple') :
          'opcion-simple'
        }
      />
    </div>
  );
};

export default TestView;